import { Injectable } from '@angular/core';
import { StoreLoader } from '@core-lib/classes';
import { TiersFormComponent } from '@core-lib/components';
import { TiersListComponent } from '@core-lib/components/tiers-list/tiers-list.component';
import { TiersPartieDetailComponent } from '@core-lib/components/tiers-partie-detail/tiers-partie-detail.component';
import { ActionType, ControlFieldIdAffaireEnum, ErrorCode, TypeAction } from '@core-lib/enums';
import { extractTiersToAutocompleteItemsForApiSearch } from '@core-lib/functions';
import { IAction, ITiersList } from '@core-lib/models';
import { TiersService } from '@core-lib/services';
import { SharedStoreActions, SharedStoreSelectors } from '@core-lib/store/shared-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ModalRef, ModalService } from '@core-lib/modules/modal-container';
import { of } from 'rxjs';
import { catchError, debounceTime, map, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { Noop } from '../init-store/actions';
import * as featureActions from './actions';
import * as featureSelectors from './selectors';

@Injectable()
export class TiersStoreEffects {
	constructor(
		private actions$: Actions,
		private store$: Store<any>,
		private tiersService: TiersService,
		private modalService: ModalService
	) {}

	OpenTiersForm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.OpenTiersForm),
			map(action => {
				const modalRef: ModalRef = this.modalService.show(TiersFormComponent, { ignoreBackdropClick: true });
				const modal: TiersFormComponent = modalRef.content;

				modal.loader = new StoreLoader(
					this.store$.select(featureSelectors.selectTiersIsLoading),
					this.store$.select(featureSelectors.selectTiersError)
				);

				modal.labelNature = action.labelNature;
				modal.isToUpdate = action.isToUpdate;
				modal.isToConsult = false;
				modal.nationalites$ = this.store$.select(SharedStoreSelectors.selectNationalites);
				modal.nationalites$.pipe(takeUntil(modal.destroySubscriptions$)).subscribe(pays => {
					if(action.isToUpdate){
						modal.selectedPay = pays?.find(x => x.id == action.tier.paysId)?.id;
					} else {
						modal.selectedPay = pays?.find(x => x.codeISO == 'FR')?.id;
					}
				});
				modal.action.pipe(takeUntil(modal.destroySubscriptions$)).subscribe((result: IAction) => {
					if (result.type === ActionType.Submit) {
						// Verification de doublon de Tiers
						this.store$.dispatch(
							featureActions.LoadTiersListRequest(
								result.value,
								action.controlId,
								action.structureId,
								modalRef?.id,
								action?.isFromOperationManagerModal,
								action.isImmediateSave,
								action.affairePartiesIds,
								action.affairePartiesTempIds
							)
						);
					} else {
						this.store$.dispatch(featureActions.CloseTiersFormModal(modalRef.id, action.isFromOperationManagerModal));
					}
				});

				modal.tiers = action.tier;

				return featureActions.SetTiersFormModal({
					modal: modalRef?.id
				});
			})
		)
	);

	// Checking duplication before create tiers
	loadTiersListRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.LoadTiersListRequest),
			withLatestFrom(this.store$.select(SharedStoreSelectors.selectAutocompleteAddedTiers)),
			switchMap(([action, state]) => {
				//vérifier si le tier est identique à un autre déjà en cache
				let tierAutocomplete = extractTiersToAutocompleteItemsForApiSearch([action.entity]);
				if (
					tierAutocomplete != null &&
					state.find(x => x.label == tierAutocomplete[0].label && x.value.tempId != tierAutocomplete[0].value.tempId)
				) {
					let tiersList: ITiersList = { items: state?.filter(x => x.label == tierAutocomplete[0].label)?.map(x => x.value) };
					return [
						featureActions.LoadTiersListSuccess({ list: tiersList }),
						featureActions.OpenTiersListModal(
							tiersList,
							action.entity,
							action.controlId,
							action.structureId,
							action.modalId,
							action.isFromOperationManagerModal,
							action.affairePartiesIds,
							action.affairePartiesTempIds
						)
					];
				}
				return this.tiersService.searchDuplicateTiers(action.entity, action.structureId).pipe(
					switchMap(list => {
						let tiersList: ITiersList = { items: list };
						if (tiersList.items.length && tiersList.items.length >= 1) {
							return [
								featureActions.LoadTiersListSuccess({ list: tiersList }),
								featureActions.OpenTiersListModal(
									tiersList,
									action.entity,
									action.controlId,
									action.structureId,
									action.modalId,
									action.isFromOperationManagerModal,
									action.affairePartiesIds,
									action.affairePartiesTempIds
								)
							];
						} else {
							return action.isImmediateSave == true
								? [
										featureActions.LoadTiersListSuccess({ list: tiersList }),
										featureActions.UpdateTiersRequest(
											action.entity,
											action.controlId,
											action.structureId,
											'',
											action.modalId,
											action.isFromOperationManagerModal
										)
									]
								: [
										featureActions.LoadTiersListSuccess({ list: tiersList }),
										featureActions.CloseTiersFormModal(action.modalId, action.isFromOperationManagerModal),
										action.controlId
											? SharedStoreActions.AddToControlAutocomplete({
													param: {
														controlId: action.controlId,
														data: [extractTiersToAutocompleteItemsForApiSearch([action.entity])[0]]
													}
												})
											: SharedStoreActions.Noop()
									];
						}
					}),
					catchError(error => of(featureActions.LoadTiersListFailure({ error })))
				);
			})
		)
	);

	openTiersListModal$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.OpenTiersListModal),
			withLatestFrom(this.store$.select(featureSelectors.selectState)),
			debounceTime(200),
			map(([action, state]) => {
				const modalRef: ModalRef = this.modalService.show(TiersListComponent, {
					class: 'modal-lg',
					ignoreBackdropClick: true
				});
				const modal: TiersListComponent = modalRef.content;
				modal.type = action.entity?.type;
				modal.data = action.list;
				modal.loader = new StoreLoader(
					this.store$.select(featureSelectors.selectTiersListIsLoading),
					this.store$.select(featureSelectors.selectTiersListError)
				);

				modal.action.pipe(takeUntil(modal.destroySubscriptions$)).subscribe((result: IAction) => {
					if (result.type === ActionType.Select) {
						modalRef.hide();
						this.store$.dispatch(featureActions.CloseTiersFormModal(action.modalId, action.isFromOperationManagerModal));
						//déclencher la popinde duplication de tier dans le cas d'une affaire
						if (
							action.controlId == ControlFieldIdAffaireEnum.AffairePartie1ControlId ||
							action.controlId == ControlFieldIdAffaireEnum.AffairePartie2ControlId
						) {
							if (
								action.affairePartiesIds?.includes(result?.value?.id) ||
								action.affairePartiesTempIds?.includes(result?.value?.tempId)
							) {
								let error: any = {};
								error.code = ErrorCode.SelectionTierAlreadyLocked;
								return this.store$.dispatch(
									SharedStoreActions.HandleErrorException({
										error: error,
										errorCode: error.code,
										typeAction: TypeAction.LockedTierToSelect
									})
								);
							}
						}
						this.store$.dispatch(
							SharedStoreActions.AddToControlAutocomplete({
								param: {
									controlId: action.controlId,
									data: extractTiersToAutocompleteItemsForApiSearch([result.value])
								}
							})
						);
					} else if (result.type === ActionType.Ignore) {
						modalRef.hide();
						this.store$.dispatch(featureActions.CloseTiersFormModal(action.modalId, action.isFromOperationManagerModal));
						if (action.controlId) {
							this.store$.dispatch(
								SharedStoreActions.AddToControlAutocomplete({
									param: {
										controlId: action.controlId,
										data: [extractTiersToAutocompleteItemsForApiSearch([action.entity])[0]]
									}
								})
							);
						}
					} else {
						modalRef.hide();
						this.store$.dispatch(featureActions.SetDisableSubmitButton({ param: false }));
					}
				});

				return featureActions.SetTiersListModal({
					param: modalRef?.id as any
				});
			})
		)
	);

	UpdateTiersRequest$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.UpdateTiersRequest),
			switchMap(action =>
				this.tiersService.createTiers(action.entity, action.structureId).pipe(
					switchMap(newTiers => [
						featureActions.UpdateTiersSuccess({ entity: action.entity }),
						featureActions.CloseTiersFormModal(action.modalId, action.isFromOperationManagerModal),
						action.controlId
							? SharedStoreActions.AddToControlAutocomplete({
									param: {
										controlId: action.controlId,
										data: [extractTiersToAutocompleteItemsForApiSearch([newTiers])[0]]
									}
								})
							: SharedStoreActions.Noop()
					]),
					catchError(error =>
						of(
							featureActions.UpdateTiersFailure({
								error
							})
						)
					)
				)
			)
		)
	);

	closePersonnePhysiqueForm$ = createEffect(() =>
		this.actions$.pipe(
			ofType(featureActions.CloseTiersFormModal),
			withLatestFrom(this.store$.select(featureSelectors.selectState)),
			debounceTime(200),
			map(([action, state]) => {
				const modalId = action['modalId'];
				const isFromOperationManagerModal = action['isFromOperationManagerModal'];
				if (modalId && isFromOperationManagerModal) {
					if (typeof modalId === 'number') 
					this.modalService.hide(modalId);
					return Noop();
				} else {
					this.modalService.hide(state.tiersFormModal);
					return featureActions.SetTiersFormModal({
						modal: null
					});
				}
			})
		)
	);

	openTiersPartieListDetail$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(featureActions.OpenTiersPartieDetailActions),
				withLatestFrom(this.store$),
				debounceTime(200),
				tap(([action, state]) => {
					const modalRef: ModalRef = this.modalService.show(TiersPartieDetailComponent, { ignoreBackdropClick: true });
					const modal: TiersPartieDetailComponent = modalRef.content;
					modal.fromFO = action.fromFO;
					modal.libelle = action.libelle;
					modal.tiersPartie = action.parties;
					modal.affaireId = action.affaireId;
					modal.structureId = action.structureId;
					modal.isCompteBancaireClos = action.isCompteBancaireClos;
					modal.action.pipe(takeUntil(modal.destroySubscriptions$)).subscribe((result: IAction) => {
						modalRef.hide();
					});
				})
			),
		{ dispatch: false }
	);
}
